import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowUpRight, Download, Mic, Podcast, Users, FileText } from "lucide-react";
import { Reveal, SectionHeading } from "../components/Motion";
import { useContent, API, LINKS } from "../lib/content";

export const BlogSection = ({ full = false, hideHeading = false }) => {
  const { posts } = useContent();
  const list = full ? posts : posts.slice(0, 3);

  return (
    <section data-testid="blog-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
      {!hideHeading && (
        <SectionHeading
          overline="Insights"
          title="Writing on AI, consulting & enterprise technology"
          description="Frameworks and field notes from real engagements - no fluff, no hype cycles."
        />
      )}
      <div className="grid md:grid-cols-3 gap-5 md:gap-6">
        {list.map((p, i) => (
          <Reveal key={p.id} delay={(i % 3) * 0.08}>
            <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${i}`} className="card-hover group rounded-2xl border border-border bg-card p-8 h-full flex flex-col hover:border-secondary/40">
              <div className="flex items-center justify-between">
                <span className="px-3 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-medium">{p.category}</span>
                <span className="font-mono text-[10px] text-muted-foreground">{p.read_time}</span>
              </div>
              <h3 className="mt-5 font-display text-lg font-medium tracking-tight leading-snug group-hover:text-secondary transition-colors duration-200">{p.title}</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">{p.excerpt}</p>
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center justify-between">
                {p.date} <span className="inline-flex items-center gap-1 text-secondary normal-case tracking-normal">Read <ArrowUpRight size={11} /></span>
              </p>
            </Link>
          </Reveal>
        ))}
      </div>
      {!full && (
        <Reveal className="mt-14">
          <Link to="/blog" data-testid="blog-more-link" className="inline-flex items-center gap-2 text-sm text-secondary hover:underline">
            All articles <ArrowUpRight size={14} />
          </Link>
        </Reveal>
      )}
    </section>
  );
};

const GATED_TITLES = ["Readiness Assessment", "AI Readiness Compass"];

const GatedDownload = ({ r, i }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [unlocked, setUnlocked] = useState(() => localStorage.getItem("aira_unlocked") === "1");

  const unlock = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/gated-unlock`, {
        email,
        resource_title: r.title,
        source_page: typeof window !== "undefined" ? window.location.pathname : "/resources",
      });
      localStorage.setItem("aira_unlocked", "1");
      setUnlocked(true);
      toast.success("Unlocked - your download is ready.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (unlocked) {
    return (
      <a href={r.file} download className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-secondary hover:underline" data-testid={`resource-download-${i}`}>
        <Download size={13} /> Download {r.badge || "file"}
      </a>
    );
  }
  return (
    <form onSubmit={unlock} className="mt-4 flex flex-col sm:flex-row gap-2" data-testid={`resource-gate-form-${i}`}>
      <input
        type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="you@company.com"
        data-testid={`resource-gate-email-${i}`}
        className="flex-1 h-10 px-4 rounded-full bg-background border border-border text-xs focus:outline-none focus:ring-2 focus:ring-secondary/50 transition-shadow duration-200"
      />
      <button
        type="submit" disabled={busy}
        data-testid={`resource-gate-submit-${i}`}
        className="h-10 px-5 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white text-xs font-semibold hover:opacity-90 transition-opacity duration-200 disabled:opacity-60 shrink-0"
      >
        {busy ? "Unlocking..." : "Unlock download"}
      </button>
    </form>
  );
};

export const ResourcesSection = ({ hideHeading = false }) => {
  const { resources } = useContent();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/newsletter`, { email });
      toast.success(data.message);
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="resources-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
      {!hideHeading && (
        <SectionHeading
          overline="Resources"
          title="Free guides, templates & frameworks"
          description="The same tools I use in paid engagements - free for teams doing the work."
        />
      )}
      <div className="grid md:grid-cols-2 gap-5 md:gap-6">
        {resources.map((r, i) => (
          <Reveal key={r.id} delay={(i % 2) * 0.08}>
            <div data-testid={`resource-card-${i}`} className="card-hover group rounded-2xl border border-border bg-card p-8 flex items-start gap-5 hover:border-secondary/40">
              <div className="h-11 w-11 shrink-0 rounded-xl bg-gradient-to-br from-violet-600/15 to-blue-600/15 border border-border flex items-center justify-center text-secondary">
                <FileText size={19} strokeWidth={1.6} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-secondary">{r.type}</span>
                  {r.badge && (
                    <span className="font-mono text-[10px] text-muted-foreground px-2 py-0.5 rounded-full border border-border">{r.badge}</span>
                  )}
                </div>
                <h3 className="mt-2 font-display text-lg font-medium tracking-tight">{r.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{r.description}</p>
                {(r.gated || GATED_TITLES.some((g) => (r.title || "").includes(g))) ? (
                  <GatedDownload r={r} i={i} />
                ) : (
                  <a href={r.file} download className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-secondary hover:underline" data-testid={`resource-download-${i}`}>
                    <Download size={13} /> Download {r.badge || "file"}
                  </a>
                )}
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-16">
        <div data-testid="newsletter-block" className="rounded-2xl border border-border bg-gradient-to-br from-violet-600/10 via-card to-blue-600/10 p-8 md:p-12">
          <div className="max-w-xl">
            <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight">The Enterprise AI Brief</h3>
            <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
              One email a month on AI strategy, analytics, and enterprise transformation. No noise, unsubscribe anytime.
            </p>
            <form onSubmit={subscribe} className="mt-6 flex flex-col sm:flex-row gap-3">
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="newsletter-email-input"
                className="flex-1 h-12 px-5 rounded-full bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-secondary/50 transition-shadow duration-200"
              />
              <button
                type="submit" disabled={busy}
                data-testid="newsletter-submit"
                className="h-12 px-7 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-opacity duration-200 disabled:opacity-60"
              >
                {busy ? "Subscribing..." : "Subscribe"}
              </button>
            </form>
          </div>
        </div>
      </Reveal>
    </section>
  );
};

const SPEAK_ICONS = { Conference: Mic, Podcast: Podcast, Workshop: Users };

export const SpeakingSection = ({ hideHeading = false }) => {
  const { speaking } = useContent();
  return (
    <section data-testid="speaking-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
      {!hideHeading && (
        <SectionHeading
          overline="Speaking"
          title="Talks, podcasts & executive workshops"
          description="Available for conferences, podcasts, and private leadership sessions on enterprise AI, analytics strategy, and value engineering."
        />
      )}
      <div className="space-y-4">
        {speaking.map((s, i) => {
          const Icon = SPEAK_ICONS[s.type] || Mic;
          return (
            <Reveal key={s.id} delay={i * 0.08}>
              <div data-testid={`speaking-card-${i}`} className="card-hover group rounded-2xl border border-border bg-card p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 hover:border-secondary/40">
                <div className="h-11 w-11 shrink-0 rounded-xl bg-gradient-to-br from-violet-600/15 to-blue-600/15 border border-border flex items-center justify-center text-secondary">
                  <Icon size={19} strokeWidth={1.6} />
                </div>
                <div className="flex-1">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-secondary">{s.type}</span>
                  <h3 className="mt-1 font-display text-lg font-medium tracking-tight">{s.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{s.topic}</p>
                </div>
                <div className="text-left md:text-right shrink-0">
                  <p className="font-mono text-xs text-muted-foreground">{s.date}</p>
                  <p className="text-xs text-muted-foreground mt-1">{s.location}</p>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
      <Reveal className="mt-12">
        <a
          href={`mailto:${LINKS.email}?subject=Speaking Inquiry`}
          data-testid="speaking-invite-link"
          className="inline-flex items-center gap-2 text-sm text-secondary hover:underline"
        >
          Invite me to speak - request media kit <ArrowUpRight size={14} />
        </a>
      </Reveal>
    </section>
  );
};
